import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Copy as CopyIcon, RefreshCw, Trash2 } from 'lucide-react';
import type { Feed } from '@new-signal/shared';
import { apiFetch } from '../lib/api';
import { track } from '../lib/analytics';
import { cn } from '../lib/utils';
import { createPublicRssUrl } from '../lib/rss-url';

const iconButtonClassName =
  'flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-brand-muted hover:bg-black/5 hover:text-brand-text disabled:cursor-wait disabled:opacity-60';

function formatRelative(
  iso: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const minutes = Math.floor((Date.now() - then) / 60_000);
  if (minutes < 1) return t('card.relative.justNow');
  if (minutes < 60) return t('card.relative.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('card.relative.hoursAgo', { count: hours });
  return t('card.relative.daysAgo', { count: Math.floor(hours / 24) });
}

export interface FeedWithCount extends Feed {
  source_count: number;
}

interface FeedCardProps {
  feed: FeedWithCount;
  onUpdate: (feed: FeedWithCount) => void;
  onDelete: (feedId: string) => void;
}

export function FeedCard({ feed, onUpdate, onDelete }: FeedCardProps) {
  const { t } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);

  const rssUrl = createPublicRssUrl(window.location.origin, feed.public_token);

  const handleToggle = async () => {
    setToggling(true);
    const res = await apiFetch(`/feeds/${feed.id}`, {
      method: 'PUT',
      body: JSON.stringify({ is_enabled: !feed.is_enabled }),
    });
    if (res.ok) {
      const updated = (await res.json()) as Feed;
      track(updated.is_enabled ? 'feed_enabled' : 'feed_disabled');
      onUpdate({ ...updated, source_count: feed.source_count });
    }
    setToggling(false);
  };

  const handleDelete = async () => {
    if (!confirm(t('card.deleteConfirm', { name: feed.name }))) return;
    setDeleting(true);
    const res = await apiFetch(`/feeds/${feed.id}`, { method: 'DELETE' });
    if (res.ok) {
      track('feed_deleted');
      onDelete(feed.id);
    }
    setDeleting(false);
  };

  const handleCopy = async () => {
    track('feed_url_copied');
    await navigator.clipboard.writeText(rssUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRotateToken = async () => {
    if (!confirm(t('card.rotateConfirm'))) return;
    setRotating(true);
    try {
      const res = await apiFetch(`/feeds/${feed.id}/public-token/rotate`, { method: 'POST' });
      if (res.ok) {
        const updated = (await res.json()) as Feed;
        track('feed_public_token_rotated');
        onUpdate({ ...updated, source_count: feed.source_count });
        setCopied(false);
      }
    } finally {
      setRotating(false);
    }
  };

  const actions = feed.actions ?? [];

  return (
    <article
      className={cn(
        'rounded-2xl border border-brand-border bg-white px-6 py-[22px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-opacity',
        !feed.is_enabled && 'opacity-60',
      )}
    >
      {/* Top row: name + controls */}
      <div className="mb-1 flex items-start justify-between gap-4">
        <Link
          to={`/feeds/${feed.id}`}
          className="min-w-0 text-xl font-bold leading-7 tracking-[-0.015em] text-brand-text no-underline hover:text-accent-blue"
        >
          {feed.name}
        </Link>
        <div className="flex shrink-0 items-center gap-2.5">
          <button
            onClick={handleRotateToken}
            disabled={rotating}
            aria-label={t('card.rotateToken')}
            title={t('card.rotateToken')}
            className={iconButtonClassName}
          >
            <RefreshCw
              size={14}
              strokeWidth={1.75}
              className={cn(rotating && 'animate-spin')}
              aria-hidden
            />
          </button>
          <span className="hidden whitespace-nowrap text-[12.5px] leading-5 text-brand-muted sm:inline">
            {feed.last_synced_at
              ? t('card.lastSyncedRelative', { time: formatRelative(feed.last_synced_at, t) })
              : t('card.lastSyncedNever')}
          </span>
          <label
            title={feed.is_enabled ? t('card.paused') : t('card.active')}
            className={cn(
              'inline-flex select-none items-center',
              toggling ? 'cursor-wait opacity-60' : 'cursor-pointer',
            )}
          >
            <span className="relative inline-flex">
              <input
                type="checkbox"
                checked={feed.is_enabled}
                onChange={handleToggle}
                disabled={toggling}
                aria-label={feed.is_enabled ? t('card.active') : t('card.paused')}
                className="peer sr-only"
              />
              <span
                aria-hidden
                className={cn(
                  'h-[22px] w-[38px] rounded-full transition-colors',
                  feed.is_enabled ? 'bg-accent-green' : 'bg-[#E9E9EB]',
                )}
              />
              <span
                aria-hidden
                className={cn(
                  'pointer-events-none absolute left-0.5 top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.25)] transition-transform',
                  feed.is_enabled && 'translate-x-4',
                )}
              />
            </span>
          </label>
          <button
            onClick={handleDelete}
            disabled={deleting}
            aria-label={tc('delete')}
            title={deleting ? t('card.deleting') : tc('delete')}
            className={cn(iconButtonClassName, 'hover:bg-brand-error/10 hover:text-brand-error')}
          >
            <Trash2 size={14} strokeWidth={1.75} aria-hidden />
          </button>
        </div>
      </div>

      {/* Second row: source + action summary */}
      <div className="mb-[18px] flex flex-wrap items-center gap-x-2 gap-y-1 text-sm leading-5 text-brand-subtle">
        <span>{t('card.sourceCount', { count: feed.source_count })}</span>
        <span aria-hidden>·</span>
        <span>{t('card.actionCount', { count: actions.length })}</span>
      </div>

      {/* RSS URL row */}
      <div className="relative mb-4">
        <input
          readOnly
          value={rssUrl}
          aria-label="RSS URL"
          className="box-border w-full rounded-[9px] border border-brand-border-medium bg-brand-surface py-2.5 pl-3 pr-10 font-mono text-[12.5px] leading-5 text-brand-text outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/10"
        />
        <button
          onClick={handleCopy}
          aria-label={copied ? tc('copied') : tc('copy')}
          title={copied ? tc('copied') : tc('copy')}
          className={cn(
            'absolute right-[5px] top-1/2 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-[7px] border-0 bg-transparent hover:bg-black/[0.06]',
            copied ? 'text-accent-green' : 'text-brand-muted',
          )}
        >
          {copied ? (
            <Check size={14} strokeWidth={2} aria-hidden />
          ) : (
            <CopyIcon size={14} strokeWidth={1.75} aria-hidden />
          )}
        </button>
      </div>
    </article>
  );
}
